import { parseRateLimitError } from "../../shared/audienceLimits.ts";
import { notificationCopy, postingTooQuicklyCopy, type NotificationMessage } from "./notificationCopy.ts";

export type AudienceAction = "join" | "post" | "reply" | "alias" | "like";

const DOMAIN_ERRORS: Array<{ text: string; message: NotificationMessage }> = [
  { text: "Posting is closed.", message: notificationCopy.participant.postingClosed },
  { text: "Replies are closed.", message: notificationCopy.participant.repliesClosed },
  { text: "Join the session before posting.", message: notificationCopy.participant.joinBeforePosting },
  { text: "Session is not open.", message: notificationCopy.participant.sessionNotOpen },
  { text: "Participant joining is closed.", message: notificationCopy.participant.sessionNotOpen },
  { text: "Post is not open for replies.", message: notificationCopy.participant.threadClosedForReplies },
  { text: "Thread is closed for replies.", message: notificationCopy.participant.threadClosedForReplies },
];

export function normalizeAudienceError(error: unknown, action: AudienceAction): NotificationMessage {
  const text = extractErrorText(error);
  const compact = compactConvexErrorText(text);
  const matchSource = `${text}\n${compact}`;
  const rateLimited = parseRateLimitError(matchSource);
  if (rateLimited) {
    return postingTooQuicklyCopy(
      rateLimited.retryAfterSeconds,
      rateLimited.kind === "replies" ? "reply" : "post",
    );
  }
  const matched = DOMAIN_ERRORS.find((entry) => matchSource.includes(entry.text));
  if (matched) return matched.message;

  if (action === "reply") return notificationCopy.participant.couldNotSendReply;
  if (action === "alias") return notificationCopy.participant.couldNotUpdateName;
  if (action === "like") return notificationCopy.participant.couldNotUpdateLike;
  if (action === "join") return notificationCopy.participant.sessionNotOpen;
  return notificationCopy.participant.couldNotPost;
}

export function compactConvexErrorText(text: string): string {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const uncaught = lines.find((line) => line.startsWith("Uncaught Error: "));
  if (uncaught) return uncaught.slice("Uncaught Error: ".length).trim();
  const errorLine = lines.find((line) => line.startsWith("Error: "));
  if (errorLine) return errorLine.slice("Error: ".length).trim();
  return lines[0] ?? text.trim();
}

function extractErrorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return String(error);
}
